import { View, Text, Image } from '@react-pdf/renderer';
import { carePhaseDefs, CarePhase, formatDurationYears, isDurationCustom } from '@shared';
import { onboardingPdfReportStyle } from '@/features/pdf-report/lib/pdf-theme';
import { getPronoun } from '@/features/pdf-report/lib/utils';
import { ShieldCheckIcon } from '../icons/pdf-icons';
import type { PdfReportData } from '@/features/pdf-report/types';

export function PredictedCareNeedsSection({ client, illustrations }: { client: Client; illustrations: PdfReportData['illustrations'] }) {
    if (!illustrations) return null;
    const { clientFirstName, intakeSurvey, phaseCosts, allPhaseCosts: { allPhaseCareMonthsNeeded } } = client;
    const { ltcAtAge } = client.appliedInferenceSet;
    const isMale = getPronoun(intakeSurvey) === 'he';
    const weeksPerMonth = 4.33;
    const ltcDurYrs = allPhaseCareMonthsNeeded / 12;

    const earlyIdx = carePhaseDefs[CarePhase.earlyCare].index;
    const earlyPC = phaseCosts[earlyIdx];
    const earlyHpw = earlyPC.phaseCareHoursNeeded / earlyPC.phaseCareMonthsNeeded / weeksPerMonth;

    const modIdx = carePhaseDefs[CarePhase.moderateCare].index;
    const modPC = phaseCosts[modIdx];

    const lateIdx = carePhaseDefs[CarePhase.fullCare].index;
    const latePC = phaseCosts[lateIdx];
    const lateHpw = latePC.phaseCareHoursNeeded / latePC.phaseCareMonthsNeeded / weeksPerMonth;

    const earlyImg = isMale ? illustrations.manEarlyAssistant : illustrations.womanEarlyAssistant;
    const lateImg = isMale ? illustrations.manLateAssistant : illustrations.womanLateAssistant;

    const earlyStartAge = Math.round(ltcAtAge);
    const modStartAge = Math.round(ltcAtAge + earlyPC.phaseCareMonthsNeeded / 12);
    const lateStartAge = Math.round(ltcAtAge + earlyPC.phaseCareMonthsNeeded / 12 + modPC.phaseCareMonthsNeeded / 12);
    const endAge = Math.round(ltcAtAge + allPhaseCareMonthsNeeded / 12);

    const earlyAges: number[] = [];
    for (let a = earlyStartAge; a < modStartAge; a++) earlyAges.push(a);
    const modAges: number[] = [];
    for (let a = modStartAge; a < lateStartAge; a++) modAges.push(a);
    const lateAges: number[] = [];
    for (let a = lateStartAge; a <= endAge; a++) lateAges.push(a);

    return (
        <View style={onboardingPdfReportStyle.optionalSectionBox} wrap={false}>
            <View style={onboardingPdfReportStyle.optionalSectionHeader}>
                <ShieldCheckIcon />
                <Text style={onboardingPdfReportStyle.optionalSectionHeaderText}>{clientFirstName}'s Predicted Care Needs</Text>
            </View>
            <View style={onboardingPdfReportStyle.optionalSectionBody}>
                <Text style={onboardingPdfReportStyle.optionalNarrative}>Later stages may require more intensive support for daily activities like bathing, dressing, and mobility.</Text>
                <View style={onboardingPdfReportStyle.optionalGrid}>
                    <View style={onboardingPdfReportStyle.illustrationCard}>
                        <View style={onboardingPdfReportStyle.illustrationWrapper}><Image style={onboardingPdfReportStyle.illustrationImage} src={earlyImg} /></View>
                        <Text style={onboardingPdfReportStyle.illustrationTitle}>Early Care</Text>
                        <Text style={onboardingPdfReportStyle.illustrationSubtitle}>Initial assistance needs</Text>
                        <View style={onboardingPdfReportStyle.hoursPill}>
                            <Text><Text style={onboardingPdfReportStyle.hoursPillValue}>{Math.round(earlyHpw)}</Text><Text style={onboardingPdfReportStyle.hoursPillUnit}> hrs/week</Text></Text>
                        </View>
                    </View>
                    <View style={onboardingPdfReportStyle.centerDuration}>
                        <Text style={onboardingPdfReportStyle.centerDurationLabel}>LENGTH OF CARE</Text>
                        <Text style={onboardingPdfReportStyle.centerDurationValue}>{formatDurationYears(ltcDurYrs)}</Text>
                        <Text style={onboardingPdfReportStyle.centerDurationUnit}>years{isDurationCustom(phaseCosts) ? ' (edited)' : ''}</Text>
                        <View style={onboardingPdfReportStyle.centerDurationLine} />
                    </View>
                    <View style={[onboardingPdfReportStyle.illustrationCard, onboardingPdfReportStyle.lateCareCard]}>
                        <View style={onboardingPdfReportStyle.illustrationWrapper}><Image style={onboardingPdfReportStyle.illustrationImage} src={lateImg} /></View>
                        <Text style={onboardingPdfReportStyle.illustrationTitle}>Later Care</Text>
                        <Text style={onboardingPdfReportStyle.illustrationSubtitle}>Intensive support needs</Text>
                        <View style={onboardingPdfReportStyle.hoursPill}>
                            <Text><Text style={onboardingPdfReportStyle.hoursPillValue}>{Math.round(lateHpw)}</Text><Text style={onboardingPdfReportStyle.hoursPillUnit}> hrs/week</Text></Text>
                        </View>
                    </View>
                </View>

                {/* Age range timeline */}
                <View style={onboardingPdfReportStyle.ageTimelineContainer}>
                    {/* Phase labels */}
                    <View style={onboardingPdfReportStyle.ageTimelineRow}>
                        <View style={[onboardingPdfReportStyle.ageTimelineSegment, { flex: earlyAges.length || 1 }]}>
                            <Text style={onboardingPdfReportStyle.ageTimelineSegmentLabel}>Early Care (Age {earlyStartAge})</Text>
                        </View>
                        <View style={[onboardingPdfReportStyle.ageTimelineSegment, { flex: modAges.length || 1 }]}>
                            <Text style={onboardingPdfReportStyle.ageTimelineSegmentLabel}>Moderate Care (Age {modStartAge})</Text>
                        </View>
                        <View style={[onboardingPdfReportStyle.ageTimelineSegment, { flex: lateAges.length || 1 }]}>
                            <Text style={onboardingPdfReportStyle.ageTimelineSegmentLabel}>Full Care (Age {lateStartAge})</Text>
                        </View>
                    </View>
                    {/* Colored line segments */}
                    <View style={onboardingPdfReportStyle.ageTimelineRow}>
                        <View style={[onboardingPdfReportStyle.ageTimelineSegmentLine, onboardingPdfReportStyle.ageTimelineSegmentLineEarly, { flex: earlyAges.length || 1 }]} />
                        <View style={[onboardingPdfReportStyle.ageTimelineSegmentLine, onboardingPdfReportStyle.ageTimelineSegmentLineMod, { flex: modAges.length || 1 }]} />
                        <View style={[onboardingPdfReportStyle.ageTimelineSegmentLine, onboardingPdfReportStyle.ageTimelineSegmentLineFull, { flex: lateAges.length || 1 }]} />
                    </View>
                    {/* Age ticks grouped under each phase */}
                    <View style={onboardingPdfReportStyle.ageTimelineRow}>
                        <View style={[onboardingPdfReportStyle.ageTimelineSegment, onboardingPdfReportStyle.ageTimelineSegmentAges, { flex: earlyAges.length || 1 }]}>
                            {earlyAges.map(a => <Text key={a} style={onboardingPdfReportStyle.ageTimelineAge}>{a}</Text>)}
                        </View>
                        <View style={[onboardingPdfReportStyle.ageTimelineSegment, onboardingPdfReportStyle.ageTimelineSegmentAges, { flex: modAges.length || 1 }]}>
                            {modAges.map(a => <Text key={a} style={onboardingPdfReportStyle.ageTimelineAge}>{a}</Text>)}
                        </View>
                        <View style={[onboardingPdfReportStyle.ageTimelineSegment, onboardingPdfReportStyle.ageTimelineSegmentAges, { flex: lateAges.length || 1 }]}>
                            {lateAges.map(a => <Text key={a} style={onboardingPdfReportStyle.ageTimelineAge}>{a}</Text>)}
                        </View>
                    </View>
                </View>
            </View>
        </View>
    );
}
